import { ChangeEvent, FC, useCallback, useLayoutEffect, useMemo, useState, MouseEvent } from 'react';
import { Button, DashboardCard, Loading, Modal, Table, Tag } from '../../../../components';
import { TConcessionaria, deleteAdminDealer, getAllAdminDealers } from '../../../../services';
import { RegistrationPage } from '../../templates';
import {
	RegistrationEntities,
	TagActive,
	TagActiveLabel,
	useFilteredItems,
} from '../../../../utils';
import {
	ActionsContainer,
	Dashboard,
	TableWrapper,
	TagsContainer,
} from '../../Registration.styles';
import { Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import { DeleteOutlined, EditOutlined, GroupsOutlined } from '@mui/icons-material';
import { useToastStore } from '../../../../stores';
import { TagDeferral } from '../../../../utils/enums/TagDeferral';
import { DashboardStatus } from '../../../../components/dashboard-card/DashboardCard';
import { TagDeferralLabel } from '../../../../utils/enums/TagDeferralLabel';

const RegistrationDealerships: FC = () => {
	const { setToast } = useToastStore();
	const [loading, setLoading] = useState<boolean>(false);
	const [concessionarias, setConcessionarias] = useState<TConcessionaria[]>([]);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [concessionariaToDelete, setConcessionariaToDelete] = useState<number>();
	const [activeCard, setActiveCard] = useState<DashboardStatus | null>(null);
	const [searchTerm, setSearchTerm] = useState('');
	const { filteredItems, filterLoading, handleFilterEstado } = useFilteredItems({
		items: concessionarias || [],
		searchTerm: searchTerm,
		status: activeCard,
		filterFn: useCallback(
			(item: TConcessionaria, searchTerm: string, status: DashboardStatus | null) => {
				let matchesSearchTerm = true;

				if (searchTerm) {
					const matchesRazaoSocial =
						item.razaoSocial?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false;
					const matchesFantasia =
						item.fantasia?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false;
					const matchesGrupo =
						item.grupoConcessionaria.grupoConcessionaria
							.toLowerCase()
							.includes(searchTerm.toLowerCase()) ?? false;
					const matchesCnpj = item.cnpj?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false;
					const matchesEmail =
						item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false;

					matchesSearchTerm =
						matchesRazaoSocial || matchesFantasia || matchesGrupo || matchesCnpj || matchesEmail;
				}

				switch (status) {
					case DashboardStatus.Active:
						return (
							matchesSearchTerm &&
							item.tagAtivo === TagActive.Active &&
							item.tagDeferimento === TagDeferral.Deferred
						);

					case DashboardStatus.Inactive:
						return (
							matchesSearchTerm &&
							item.tagAtivo === TagActive.Inactive &&
							item.tagDeferimento === TagDeferral.Deferred
						);

					case DashboardStatus.Pending:
						return matchesSearchTerm && item.tagDeferimento === TagDeferral.Pending;

					default:
						return matchesSearchTerm;
				}
			},
			[]
		),
	});

	const activeConcessionarias = useMemo(
		() =>
			concessionarias?.filter(
				(concessionaria) =>
					concessionaria.tagAtivo === TagActive.Active &&
					concessionaria.tagDeferimento === TagDeferral.Deferred
			),
		[concessionarias]
	);

	const inactiveConcessionarias = useMemo(
		() =>
			concessionarias?.filter(
				(concessionaria) =>
					concessionaria.tagAtivo === TagActive.Inactive &&
					concessionaria.tagDeferimento === TagDeferral.Deferred
			),
		[concessionarias]
	);

	const pendingConcessionarias = useMemo(
		() =>
			concessionarias?.filter(
				(concessionaria) => concessionaria.tagDeferimento === TagDeferral.Pending
			),
		[concessionarias]
	);

	const tableColumns = useMemo(
		() => [
			{ id: 'fantasia', label: 'Nome Fantasia' },
			{ id: 'group', label: 'Grupo Concessionária' },
			{ id: 'cnpj', label: 'CNPJ' },
			{ id: 'status', label: 'Status' },
			{ id: 'actions', label: 'Ações' },
		],
		[]
	);

	const defineStatusLabel = useCallback((concessionaria: TConcessionaria) => {
		if (concessionaria.tagAtivo === TagActive.Active) {
			return TagActiveLabel.Active;
		}

		return TagActiveLabel.Inactive;
	}, []);

	const defineActiveLabel = useCallback(
		(concessionaria: TConcessionaria) => {
			if (concessionaria.tagDeferimento === TagDeferral.Pending) {
				return TagDeferralLabel.Pending;
			}

			return defineStatusLabel(concessionaria);
		},
		[defineStatusLabel]
	);

	const handleDeleteConfirm = useCallback((id: number) => {
		setOpenDeleteModal(true);
		setConcessionariaToDelete(id);
	}, []);

	const rowMapper = useCallback(
		(item: TConcessionaria) => ({
			fantasia: { value: item.fantasia, rawValue: item.fantasia },
			group: {
				value: item.grupoConcessionaria.grupoConcessionaria,
				rawValue: item.grupoConcessionaria.grupoConcessionaria,
			},
			cnpj: { value: item.cnpj, rawValue: item.cnpj },
			status: {
				value: (
					<TagsContainer>
						<Tag status={defineActiveLabel(item)} />
					</TagsContainer>
				),
				rawValue: defineActiveLabel(item).toString(),
			},
			actions: {
				value: (
					<ActionsContainer>
						<Tooltip title='Editar' placement='top' arrow>
							<Link to={`editar/${item.id}`}>
								<Button variant='icon' icon={<EditOutlined fontSize='inherit' />} />
							</Link>
						</Tooltip>
						<Tooltip title='Desativar' placement='top' arrow>
							<div>
								<Button
									variant='icon'
									icon={<DeleteOutlined fontSize='inherit' />}
									onClick={() => handleDeleteConfirm(item.id)}
								/>
							</div>
						</Tooltip>
						<Tooltip title='Usuários' placement='top' arrow>
							<Link to={`/cadastros/usuarios/concessionaria-${item.id}`}>
								<Button variant='icon' icon={<GroupsOutlined fontSize='inherit' />} />
							</Link>
						</Tooltip>
					</ActionsContainer>
				),
			},
		}),
		[handleDeleteConfirm, defineActiveLabel]
	);

	const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value);
		// Solicitação de quem manda até verem que não vale a pena. Nao demora pra remover
	};

	const handleDelete = async () => {
		try {
			const response = await deleteAdminDealer({ id: concessionariaToDelete!.toString() });

			if (!response.includes('sucesso')) {
				setToast({ message: response, variant: 'error' });
			} else {
				setToast({
					message: 'Concessionária removida com sucesso!',
					variant: 'success',
				});
				setConcessionarias((prevConcessionarias) =>
					prevConcessionarias?.filter(
						(concessionaria) => concessionaria.id !== concessionariaToDelete
					)
				);
			}
		} catch {
			setToast({
				message: 'Erro ao remover a Concessionária!',
				variant: 'error',
			});
		} finally {
			setOpenDeleteModal(false);
		}
	};

	const handleFilterChange = (status: DashboardStatus) => {
		if (status === activeCard) {
			setActiveCard(null);

			return;
		}

		setActiveCard(status);
	};

	const handleClearSearch = (e: MouseEvent<HTMLButtonElement>) => {
		setSearchTerm('');
	}

	const MemoizedTable = useMemo(() => {
		if (filteredItems?.length === 0) {
		  return <div style={{width:"100%",padding:"20px 0px", textAlign:"center"}}>Sem resultados para sua pesquisa de concessionárias.</div>; // Exibe mensagem caso não haja itens
		}
		return <Table columns={tableColumns} rows={filteredItems?.map(rowMapper)} paginate />;
	  }, [filteredItems, rowMapper, tableColumns]);
	  

	useLayoutEffect(() => {
		const fetchData = async () => {
			setLoading(true);
			try {
				const response = await getAllAdminDealers();
				const sorted = (response || []).sort((a, b) => {
					if (a.fantasia! < b.fantasia!) {
						return -1;
					}
					if (a.fantasia! > b.fantasia!) {
						return 1;
					}
					return 0;
				});
				setConcessionarias(sorted || []);
			} catch {
				setToast({
					message: 'Erro ao buscar as concessionárias!',
					variant: 'error',
				});
			} finally {
				setTimeout(() => setLoading(false), 500);
			}
		};

		fetchData();
	}, [setToast]);

	if (loading) return <Loading fullScreen />;

	return (
		<>
			<Dashboard>
				<DashboardCard
					active={activeCard === DashboardStatus.Active}
					variant={DashboardStatus.Active}
					amount={activeConcessionarias?.length ?? 0}
					onClick={() => handleFilterChange(DashboardStatus.Active)}
				/>
				<DashboardCard
					active={activeCard === DashboardStatus.Inactive}
					variant={DashboardStatus.Inactive}
					amount={inactiveConcessionarias?.length ?? 0}
					onClick={() => handleFilterChange(DashboardStatus.Inactive)}
				/>
				<DashboardCard
					active={activeCard === DashboardStatus.Pending}
					variant={DashboardStatus.Pending}
					amount={pendingConcessionarias?.length ?? 0}
					onClick={() => handleFilterChange(DashboardStatus.Pending)}
				/>
			</Dashboard>
			<RegistrationPage
				entity={RegistrationEntities.dealership}
				searchTerm={searchTerm}
				onChange={handleSearchChange}
				onClear={handleClearSearch}
				handleFilterEstado={handleFilterEstado}
				filteredItems={filteredItems}
				
			>
				<>
					<Modal
						open={openDeleteModal}
						onClose={() => setOpenDeleteModal(false)}
						title='Confirmação!'
						primaryButtonText='Sim'
						secondaryButtonText='Não'
						onPrimaryButtonClick={handleDelete}
						onSecondaryButtonClick={() => setOpenDeleteModal(false)}
					>
						Deseja realmente desativar?
					</Modal>
					<TableWrapper>{filterLoading ? <Loading /> : MemoizedTable}</TableWrapper>
				</>
			</RegistrationPage>
		</>
	);
};

export default RegistrationDealerships;
